<template>
  <div class="table-container">
    <div class="fixed-header">
      <div>
        <el-button type="primary" :disabled="loading" @click="openDialog(null)">新建页面</el-button>
      </div>
      <p class="hint">页面列表拉取配置：<code>GET /api/v1/passthrough/pages?skip=0&amp;limit=200</code></p>
    </div>

    <div class="table-wrapper">
      <el-table v-loading="loading" :data="data" height="calc(100vh - 200px)">
        <el-table-column label="ID" width="90" prop="id" show-overflow-tooltip />
        <el-table-column label="页面名称" width="160" prop="pageName" />
        <el-table-column label="地址 URL" min-width="240" prop="pageUrl" show-overflow-tooltip />
        <el-table-column label="启用" width="90">
          <template #default="scope">
            <el-tag :type="scope.row.enabled ? 'success' : 'info'">
              {{ scope.row.enabled ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="插屏" width="90">
          <template #default="scope">
            <el-tag :type="scope.row.interstitialEnabled ? 'warning' : 'info'">
              {{ scope.row.interstitialEnabled ? '开' : '关' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="170" prop="updatedAt" />
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button size="small" :disabled="loading" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" :disabled="loading" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="fixed-pagination">
      <div style="display: flex; justify-content: space-between">
        <el-pagination
          layout="prev, pager, next"
          :total="total"
          :page-size="query.limit || 50"
          @current-change="pageChange"
          :disabled="loading"
        />
        <el-select v-model="query.limit" style="width: 100px" @change="reload" :disabled="loading">
          <el-option :value="20" />
          <el-option :value="50" />
          <el-option :value="100" />
        </el-select>
      </div>
    </div>

    <el-dialog v-model="dialogOpened" :title="editing ? '编辑页面' : '新建页面'" :before-close="reset">
      <el-form :model="form" label-width="120px">
        <el-form-item label="页面名称" required>
          <el-input v-model="form.pageName" placeholder="展示用名称" />
        </el-form-item>
        <el-form-item label="地址 URL" required>
          <el-input v-model="form.pageUrl" type="textarea" :rows="2" placeholder="https://..." />
        </el-form-item>
        <el-form-item label="是否启用">
          <el-switch v-model="form.enabled" />
        </el-form-item>
        <el-form-item label="开启插屏">
          <el-switch v-model="form.interstitialEnabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="loading" text @click="reset">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { PassthroughPageApi, PassthroughPageRow } from '../../api/passthroughApi';

export default defineComponent({
  name: 'PassthroughPage',
  data() {
    return {
      loading: false,
      data: [] as PassthroughPageRow[],
      editing: null as PassthroughPageRow | null,
      dialogOpened: false,
      total: 0,
      page: 1,
      query: { limit: 50, skip: 0 },
      form: {
        pageUrl: '',
        pageName: '',
        enabled: true,
        interstitialEnabled: false,
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const result = await PassthroughPageApi.list(this.query.skip, this.query.limit);
        this.data = result.list;
        this.total = result.total;
      } catch (e) {
        ElMessage.error('加载失败');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    pageChange(page: number) {
      this.query.skip = (page - 1) * this.query.limit;
      this.init();
    },
    reload() {
      this.query.skip = 0;
      this.page = 1;
      this.init();
    },
    openDialog(row: PassthroughPageRow | null) {
      this.editing = row;
      if (row) {
        this.form = {
          pageUrl: row.pageUrl,
          pageName: row.pageName,
          enabled: row.enabled,
          interstitialEnabled: row.interstitialEnabled,
        };
      } else {
        this.form = {
          pageUrl: '',
          pageName: '',
          enabled: true,
          interstitialEnabled: false,
        };
      }
      this.dialogOpened = true;
    },
    reset() {
      this.dialogOpened = false;
      this.editing = null;
    },
    async submit() {
      if (!this.form.pageName.trim() || !this.form.pageUrl.trim()) {
        ElMessage.warning('请填写页面名称与地址');
        return;
      }
      this.loading = true;
      try {
        if (this.editing) {
          await PassthroughPageApi.update(this.editing.id, { ...this.form });
          ElMessage.success('已保存');
        } else {
          await PassthroughPageApi.create({ ...this.form });
          ElMessage.success('已创建');
        }
        this.reset();
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '操作失败');
      } finally {
        this.loading = false;
      }
    },
    async remove(row: PassthroughPageRow) {
      try {
        await ElMessageBox.confirm(`确定删除「${row.pageName}」？`, '确认', { type: 'warning' });
      } catch {
        return;
      }
      this.loading = true;
      try {
        await PassthroughPageApi.remove(row.id);
        ElMessage.success('已删除');
        await this.init();
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '删除失败');
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped>
.table-container {
  padding: 16px;
}
.fixed-header {
  margin-bottom: 12px;
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.table-wrapper {
  margin-bottom: 12px;
}
.fixed-pagination {
  padding: 8px 0;
}
</style>
